import { useRef, useState } from "react";
import { Bookmark, CalendarDays, ChevronRight, Funnel, RotateCcw, Save, Trash2, X } from "lucide-react";

import PropertySelector from "@/components/MultiSelectCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// ── Standard tick-box filter (for properties other than pipeline/stages) ──────

const INCLUDE_EMPTY_SENTINEL = "__empty__";

function TickBoxFilter({ label, options, values, onToggle, onRemove, removable, testId, excludeMode, excludeValues, onModeToggle, onExcludeToggle, allowIncludeEmpty = false }) {
  const hasExclude = typeof excludeMode !== "undefined";
  const activeValues = hasExclude && excludeMode ? (excludeValues || []) : values;
  const totalBadge = hasExclude ? (values.length + (excludeValues || []).length) : values.length;

  return (
    <details className="hubspot-dropdown relative" data-testid={`${testId}-group`}>
      <summary
        className={`hubspot-summary flex cursor-pointer list-none items-center justify-between gap-3 rounded-sm border px-3 py-2 text-sm font-medium transition hover:bg-[#f5f5f5] ${hasExclude && excludeMode ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]" : "border-[#cccccc] bg-white text-[#323c44]"}`}
        data-testid={`${testId}-summary`}
      >
        <div className="flex items-center gap-2">
          <span>{hasExclude && excludeMode ? `Excluding ${label.toLowerCase()}` : label}</span>
          {totalBadge > 0 && (
            <Badge
              variant="secondary"
              className={`rounded-sm px-1.5 py-0 text-[11px] font-semibold ${hasExclude && excludeMode ? "bg-[#fde8e8] text-[#119996]" : "bg-[#efefef] text-[#4a5b68]"}`}
              data-testid={`${testId}-count`}
            >
              {totalBadge}
            </Badge>
          )}
        </div>
        {removable && (
          <button
            className="ml-1 rounded p-0.5 text-[#7d8b96] hover:bg-[#fde8e8] hover:text-[#119996]"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove?.(); }}
            data-testid={`${testId}-remove`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </summary>
      <div className="absolute left-0 top-full z-20 mt-2 w-72 rounded-md border border-[#cccccc] bg-white shadow-lg">
        {hasExclude && (
          <div className="flex border-b border-[#efefef]">
            <button
              type="button"
              onClick={() => excludeMode && onModeToggle?.()}
              className={`flex-1 py-2 text-xs font-semibold transition ${!excludeMode ? "bg-[#f5f5f5] text-[#323c44]" : "text-[#7d8b96] hover:bg-[#f5f5f5]"}`}
            >
              Include
            </button>
            <button
              type="button"
              onClick={() => !excludeMode && onModeToggle?.()}
              className={`flex-1 py-2 text-xs font-semibold transition ${excludeMode ? "bg-[#fde8e8] text-[#119996]" : "text-[#7d8b96] hover:bg-[#f5f5f5]"}`}
            >
              Exclude
            </button>
          </div>
        )}
        <div className="max-h-52 space-y-2 overflow-y-auto p-4 pr-3">
          {options.length ? (
            options.map((option) => {
              const checked = activeValues.includes(option);
              const handleChange = hasExclude && excludeMode ? () => onExcludeToggle?.(option) : () => onToggle(option);
              return (
                <label
                  key={option}
                  className="flex items-center gap-3 rounded-sm px-1 py-1 text-sm text-[#323c44]"
                  data-testid={`${testId}-${option}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={handleChange}
                    className={`h-4 w-4 rounded border-[#cccccc] ${hasExclude && excludeMode ? "accent-[#50e3c2]" : "accent-[#50e3c2]"}`}
                    data-testid={`${testId}-${option}-checkbox`}
                  />
                  <span>{option}</span>
                </label>
              );
            })
          ) : (
            <p className="text-sm text-[#4a5b68]" data-testid={`${testId}-empty`}>
              Options appear after the first sync.
            </p>
          )}
        </div>
        {allowIncludeEmpty && options.length > 0 && (
          <div className="border-t border-[#efefef] px-4 py-2.5">
            <label className="flex items-center gap-3 rounded-sm px-1 py-1 text-sm text-[#4a5b68] cursor-pointer">
              <input
                type="checkbox"
                checked={values.includes(INCLUDE_EMPTY_SENTINEL)}
                onChange={() => onToggle(INCLUDE_EMPTY_SENTINEL)}
                className="h-4 w-4 rounded border-[#cccccc] accent-[#50e3c2]"
              />
              <span className="italic">Include deals with no value set</span>
            </label>
          </div>
        )}
      </div>
    </details>
  );
}

// ── Hierarchical Pipeline → Stages filter ─────────────────────────────────────

function PipelineRow({ pipeline, stages, isPipelineSelected, selectedStages, onTogglePipeline, onToggleStage }) {
  const [expanded, setExpanded] = useState(false);
  const selectedInPipeline = stages.filter((s) => selectedStages.includes(s)).length;

  return (
    <div>
      <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-[#f5f5f5]">
        <input
          type="checkbox"
          checked={isPipelineSelected}
          onChange={() => onTogglePipeline(pipeline)}
          className="h-4 w-4 flex-shrink-0 rounded border-[#cccccc] accent-[#50e3c2]"
        />
        <span className="flex-1 truncate text-sm font-medium text-[#323c44]">{pipeline}</span>
        {stages.length > 0 && (
          <>
            {selectedInPipeline > 0 && (
              <span className="shrink-0 text-[11px] font-semibold text-[#4a5b68]">
                {selectedInPipeline}/{stages.length}
              </span>
            )}
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="shrink-0 rounded p-0.5 text-[#7d8b96] transition hover:bg-[#efefef] hover:text-[#323c44]"
              title={expanded ? "Collapse stages" : "Expand stages"}
            >
              <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
            </button>
          </>
        )}
      </div>
      {expanded && stages.length > 0 && (
        <div className="ml-4 border-l border-[#efefef] pl-2">
          {stages.map((stage) => (
            <label
              key={stage}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-sm text-[#4a5b68] hover:bg-[#f5f5f5]"
            >
              <input
                type="checkbox"
                checked={selectedStages.includes(stage)}
                onChange={() => onToggleStage(stage)}
                className="h-4 w-4 flex-shrink-0 rounded border-[#cccccc] accent-[#50e3c2]"
              />
              <span className="truncate">{stage}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineStageFilter({ filterOptions, filters, onChange }) {
  const stagesByPipeline = filterOptions.stages_by_pipeline || {};
  const allPipelines = filterOptions.pipelines || [];
  const allStages = filterOptions.stages || [];

  const selectedPipelines = filters.pipelines || [];
  const selectedStages = filters.stages || [];

  const hasPipelineData = Object.keys(stagesByPipeline).length > 0;

  // Stages not belonging to any pipeline (orphans shown at bottom)
  const assignedStages = new Set(Object.values(stagesByPipeline).flat());
  const orphanStages = allStages.filter((s) => !assignedStages.has(s));

  const totalSelected = selectedPipelines.length + selectedStages.length;

  const togglePipeline = (pipeline) => {
    const has = selectedPipelines.includes(pipeline);
    onChange("pipelines", has ? selectedPipelines.filter((p) => p !== pipeline) : [...selectedPipelines, pipeline]);
  };

  const toggleStage = (stage) => {
    const has = selectedStages.includes(stage);
    onChange("stages", has ? selectedStages.filter((s) => s !== stage) : [...selectedStages, stage]);
  };

  // Pipelines to show: use the hierarchy order from stagesByPipeline, then append
  // any pipelines that appear in filterOptions.pipelines but not in stagesByPipeline
  const hierarchyPipelines = Object.keys(stagesByPipeline);
  const extraPipelines = allPipelines.filter((p) => !hierarchyPipelines.includes(p));
  const orderedPipelines = hasPipelineData ? [...hierarchyPipelines, ...extraPipelines] : allPipelines;

  return (
    <details className="hubspot-dropdown relative" data-testid="pipeline-stage-filter-group">
      <summary
        className={`hubspot-summary flex cursor-pointer list-none items-center gap-3 rounded-sm border px-3 py-2 text-sm font-medium transition hover:bg-[#f5f5f5] ${totalSelected > 0 ? "border-[#57c1a9] bg-[#e8faf5] text-[#323c44]" : "border-[#cccccc] bg-white text-[#323c44]"}`}
        data-testid="pipeline-stage-filter-summary"
      >
        <span>Pipelines &amp; Stages</span>
        {totalSelected > 0 && (
          <Badge
            variant="secondary"
            className="rounded-sm bg-[#efefef] px-1.5 py-0 text-[11px] font-semibold text-[#4a5b68]"
            data-testid="pipeline-stage-filter-count"
          >
            {totalSelected}
          </Badge>
        )}
      </summary>

      <div className="absolute left-0 top-full z-20 mt-2 w-80 rounded-md border border-[#cccccc] bg-white shadow-lg">
        {/* Clear selection shortcut */}
        {totalSelected > 0 && (
          <div className="flex items-center justify-between border-b border-[#efefef] px-3 py-1.5">
            <span className="text-xs text-[#4a5b68]">{totalSelected} selected</span>
            <button
              type="button"
              className="text-xs font-medium text-[#119996] hover:underline"
              onClick={() => { onChange("pipelines", []); onChange("stages", []); }}
            >
              Clear all
            </button>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto py-2">
          {orderedPipelines.length === 0 && orphanStages.length === 0 ? (
            <p className="px-4 py-3 text-sm text-[#4a5b68]">Options appear after the first sync.</p>
          ) : hasPipelineData ? (
            <>
              {orderedPipelines.map((pipeline) => (
                <PipelineRow
                  key={pipeline}
                  pipeline={pipeline}
                  stages={stagesByPipeline[pipeline] || []}
                  isPipelineSelected={selectedPipelines.includes(pipeline)}
                  selectedStages={selectedStages}
                  onTogglePipeline={togglePipeline}
                  onToggleStage={toggleStage}
                />
              ))}
              {orphanStages.length > 0 && (
                <div className="mt-1 border-t border-[#efefef] pt-1">
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#7d8b96]">
                    Other stages
                  </p>
                  {orphanStages.map((stage) => (
                    <label
                      key={stage}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 text-sm text-[#4a5b68] hover:bg-[#f5f5f5]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStages.includes(stage)}
                        onChange={() => toggleStage(stage)}
                        className="h-4 w-4 flex-shrink-0 rounded border-[#cccccc] accent-[#50e3c2]"
                      />
                      <span className="truncate">{stage}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Flat fallback when stages_by_pipeline not yet loaded */
            <div className="space-y-1 px-2">
              {allPipelines.map((p) => (
                <label key={p} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#323c44] hover:bg-[#f5f5f5]">
                  <input type="checkbox" checked={selectedPipelines.includes(p)} onChange={() => togglePipeline(p)} className="h-4 w-4 accent-[#50e3c2]" />
                  <span>{p}</span>
                </label>
              ))}
              {allStages.map((s) => (
                <label key={s} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#4a5b68] hover:bg-[#f5f5f5]">
                  <input type="checkbox" checked={selectedStages.includes(s)} onChange={() => toggleStage(s)} className="h-4 w-4 accent-[#50e3c2]" />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </details>
  );
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");
const toMonthStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const toDateStr  = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const relMonth = (offset) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return toMonthStr(d);
};

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Compute fiscal-year-aware month presets given a 1-indexed FY start month. */
function buildMonthPresets(fyStartMonth = 7) {
  const now = new Date();
  const yr  = now.getFullYear();
  const mo  = now.getMonth() + 1; // 1-indexed

  // Current FY start year
  const fyStartYear = mo >= fyStartMonth ? yr : yr - 1;
  const fyEndMonth  = fyStartMonth === 1 ? 12 : fyStartMonth - 1;
  const fyEndYear   = fyStartMonth === 1 ? fyStartYear : fyStartYear + 1;

  const fyStartLabel = `${MONTH_NAMES[fyStartMonth - 1]} ${fyStartYear}`;
  const fyEndLabel   = `${MONTH_NAMES[fyEndMonth - 1]} ${fyEndYear}`;
  const lastFyStartYear = fyStartYear - 1;

  return [
    { label: "This month",                 sub: "",               getValue: () => relMonth(0) },
    { label: "Last month",                 sub: "",               getValue: () => relMonth(-1) },
    { label: "3 months ago",               sub: "",               getValue: () => relMonth(-3) },
    { label: "6 months ago",               sub: "",               getValue: () => relMonth(-6) },
    { label: "Start of this financial year", sub: fyStartLabel,   getValue: () => `${fyStartYear}-${pad(fyStartMonth)}` },
    { label: "End of this financial year",   sub: fyEndLabel,     getValue: () => `${fyEndYear}-${pad(fyEndMonth)}` },
    { label: "Start of last financial year", sub: `${MONTH_NAMES[fyStartMonth-1]} ${lastFyStartYear}`, getValue: () => `${lastFyStartYear}-${pad(fyStartMonth)}` },
    { label: "Start of this year",         sub: `Jan ${yr}`,      getValue: () => `${yr}-01` },
    { label: "Start of last year",         sub: `Jan ${yr - 1}`,  getValue: () => `${yr - 1}-01` },
    { label: "End of this year",           sub: `Dec ${yr}`,      getValue: () => `${yr}-12` },
  ];
}

const DATE_PRESETS = [
  { label: "Today",              sub: "All of today",              getValue: () => toDateStr(new Date()) },
  { label: "Yesterday",         sub: "The previous day",          getValue: () => { const d = new Date(); d.setDate(d.getDate()-1); return toDateStr(d); } },
  { label: "Tomorrow",          sub: "The next day",              getValue: () => { const d = new Date(); d.setDate(d.getDate()+1); return toDateStr(d); } },
  { label: "Start of this month",sub: "",                         getValue: () => toDateStr(new Date(new Date().getFullYear(), new Date().getMonth(), 1)) },
  { label: "End of this month", sub: "",                          getValue: () => toDateStr(new Date(new Date().getFullYear(), new Date().getMonth()+1, 0)) },
  { label: "Start of last month",sub: "",                         getValue: () => toDateStr(new Date(new Date().getFullYear(), new Date().getMonth()-1, 1)) },
  { label: "End of last month", sub: "",                          getValue: () => toDateStr(new Date(new Date().getFullYear(), new Date().getMonth(), 0)) },
  { label: "Start of this year",sub: `1 Jan ${new Date().getFullYear()}`, getValue: () => toDateStr(new Date(new Date().getFullYear(), 0, 1)) },
  { label: "End of this year",  sub: `31 Dec ${new Date().getFullYear()}`, getValue: () => toDateStr(new Date(new Date().getFullYear(), 11, 31)) },
  { label: "Start of last year",sub: `1 Jan ${new Date().getFullYear()-1}`, getValue: () => toDateStr(new Date(new Date().getFullYear()-1, 0, 1)) },
  { label: "End of last year",  sub: `31 Dec ${new Date().getFullYear()-1}`, getValue: () => toDateStr(new Date(new Date().getFullYear()-1, 11, 31)) },
];

/** Dropdown picker with preset relative options + a custom raw input at the bottom */
function DateDropdown({ label, value, onChange, presets, inputType, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const handleBlur = (e) => {
    if (!ref.current?.contains(e.relatedTarget)) setOpen(false);
  };

  const filtered = search.trim()
    ? presets.filter((p) => p.label.toLowerCase().includes(search.toLowerCase()))
    : presets;

  const displayValue = value || placeholder;
  const hasValue = !!value;

  return (
    <div className="relative" ref={ref} onBlur={handleBlur} data-testid={testId}>
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 w-full items-center gap-2 rounded-sm border px-3 text-sm transition ${hasValue ? "border-[#57c1a9] bg-[#e8faf5] text-[#323c44]" : "border-[#cccccc] bg-[#f5f5f5] text-[#7d8b96]"} hover:border-[#57c1a9]`}
        data-testid={`${testId}-trigger`}
      >
        <CalendarDays className="h-3.5 w-3.5 flex-shrink-0 text-[#4a5b68]" />
        <span className="flex-1 text-left">{displayValue}</span>
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            className="ml-1 rounded p-0.5 text-[#7d8b96] hover:text-[#119996]"
            onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
            onKeyDown={(e) => e.key === "Enter" && onChange("")}
          >
            <X className="h-3 w-3" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1.5 w-64 rounded-md border border-[#cccccc] bg-white shadow-lg">
          <div className="border-b border-[#efefef] p-2">
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-2 text-sm text-[#323c44] placeholder:text-[#7d8b96] focus:outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="w-full px-3 py-2 text-left transition hover:bg-[#f5f5f5]"
                onClick={() => { onChange(preset.getValue()); setOpen(false); setSearch(""); }}
              >
                <p className="text-sm font-medium text-[#323c44]">{preset.label}</p>
                {preset.sub && <p className="text-xs text-[#7d8b96]">{preset.sub}</p>}
              </button>
            ))}
          </div>
          <div className="border-t border-[#efefef] p-2">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[#7d8b96]">Custom date</p>
            <input
              type={inputType}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-2 text-sm text-[#323c44] focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const currentMonth = new Date();
const monthValue = (date) => `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}`;

export function buildFyDates(fyStart, offset = 0) {
  const now = new Date();
  const thisMonth = now.getMonth() + 1;
  const currentFyStartYear = thisMonth >= fyStart ? now.getFullYear() : now.getFullYear() - 1;
  const targetFyStartYear = currentFyStartYear + offset;
  const fyEndMonth = fyStart === 1 ? 12 : fyStart - 1;
  const fyEndYear  = fyStart === 1 ? targetFyStartYear : targetFyStartYear + 1;
  return {
    from: `${targetFyStartYear}-${String(fyStart).padStart(2, "0")}`,
    to:   `${fyEndYear}-${String(fyEndMonth).padStart(2, "0")}`,
  };
}

// Keys that are always disabled in the property selector (handled by dedicated components)
const HARDCODED_KEYS = ["stages", "pipelines"];

export default function FilterBar({
  filterOptions,
  filters,
  onChange,
  onApply,
  onReset,
  activePropertyKeys,
  onAddProperty,
  onRemoveProperty,
  onToggleDynamic,
  onToggleLineItem,
  onRefreshProperties,
  presets = [],
  onSavePreset,
  onLoadPreset,
  onDeletePreset,
  fyStartMonth = 7,
  onQuickSetProbability,
}) {
  const [presetName, setPresetName] = useState("");
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);

  // Build fiscal-year-aware month presets (recomputed only when fyStartMonth changes)
  const MONTH_PRESETS = buildMonthPresets(fyStartMonth);

  const closedWon = filterOptions.stages.find(
    (item) => item?.toLowerCase().includes("closed") && item?.toLowerCase().includes("won")
  );

  const setPresetWindow = (monthsAhead = 11) => {
    const start = new Date(currentMonth.getFullYear(), 0, 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthsAhead, 1);
    onChange("fromMonth", monthValue(start));
    onChange("toMonth", monthValue(end));
  };

  const setFyWindow = (offset = 0) => {
    const { from, to } = buildFyDates(fyStartMonth, offset);
    onChange("fromMonth", from);
    onChange("toMonth", to);
  };

  const toggleOption = (key, value) => {
    const hasValue = filters[key].includes(value);
    const nextValues = hasValue ? filters[key].filter((item) => item !== value) : [...filters[key], value];
    onChange(key, nextValues);
  };

  const handleSave = () => {
    if (!presetName.trim()) return;
    onSavePreset?.(presetName.trim());
    setPresetName("");
    setPresetMenuOpen(false);
  };

  const availableProperties = filterOptions.available_properties || [];
  const getPropertyMeta = (key) => availableProperties.find((p) => p.key === key);

  // Only user-added properties (not the hardcoded pipeline/stages)
  const userActiveKeys = (activePropertyKeys || []).filter((k) => !HARDCODED_KEYS.includes(k));

  return (
    <Card className="overflow-visible border-[#cccccc] shadow-sm" data-testid="report-filter-bar">
      <CardHeader className="border-b border-[#cccccc] bg-white px-5 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 font-sans text-lg text-[#323c44]" data-testid="report-filter-title">
              <Funnel className="h-4 w-4 text-[#119996]" />
              Filters
            </CardTitle>
            <p className="mt-1 text-sm text-[#4a5b68]" data-testid="report-filter-description">
              Select pipelines and stages, then use "Add filter" to further refine by deal or line item properties.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Popover open={presetMenuOpen} onOpenChange={setPresetMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-9 gap-1.5 rounded-sm border-[#cccccc] px-3 text-[#323c44] hover:bg-[#f5f5f5]"
                  data-testid="preset-menu-button"
                >
                  <Bookmark className="h-3.5 w-3.5" />
                  Presets
                  {presets.length > 0 && (
                    <Badge variant="secondary" className="ml-1 rounded-sm bg-[#efefef] px-1.5 py-0 text-[11px] font-semibold text-[#4a5b68]">
                      {presets.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0" data-testid="preset-popover">
                <div className="border-b border-[#efefef] p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[#4a5b68]">Save current filters</p>
                  <div className="flex gap-2">
                    <input
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                      placeholder="Preset name..."
                      className="h-8 flex-1 rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-2 text-sm text-[#323c44] placeholder:text-[#7d8b96] focus:outline-none"
                      data-testid="preset-name-input"
                    />
                    <Button
                      size="sm"
                      className="h-8 rounded-sm bg-[#50e3c2] px-2 text-[#323c44] hover:bg-[#57c1a9]"
                      onClick={handleSave}
                      disabled={!presetName.trim()}
                      data-testid="preset-save-button"
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {presets.length === 0 ? (
                    <p className="px-3 py-4 text-center text-sm text-[#4a5b68]" data-testid="preset-empty-state">
                      No saved presets yet.
                    </p>
                  ) : (
                    presets.map((preset) => (
                      <div
                        key={preset.id}
                        className="flex items-center justify-between gap-2 border-b border-[#f5f5f5] px-3 py-2 last:border-0 hover:bg-[#f5f5f5]"
                        data-testid={`preset-item-${preset.id}`}
                      >
                        <button
                          className="flex-1 text-left text-sm font-medium text-[#323c44] hover:text-[#119996]"
                          onClick={() => { onLoadPreset?.(preset); setPresetMenuOpen(false); }}
                          data-testid={`preset-load-${preset.id}`}
                        >
                          {preset.name}
                        </button>
                        <button
                          className="shrink-0 rounded p-1 text-[#7d8b96] hover:bg-[#fde8e8] hover:text-[#119996]"
                          onClick={() => onDeletePreset?.(preset.id)}
                          data-testid={`preset-delete-${preset.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" className="h-9 rounded-sm border-[#cccccc] px-3 text-[#323c44] hover:bg-[#f5f5f5]" onClick={() => setFyWindow(-1)} data-testid="preset-last-fy-button">
              Last FY
            </Button>
            <Button variant="outline" className="h-9 rounded-sm border-[#cccccc] px-3 text-[#323c44] hover:bg-[#f5f5f5]" onClick={() => setFyWindow(0)} data-testid="preset-current-fy-button">
              Current FY
            </Button>
            <Button variant="outline" className="h-9 rounded-sm border-[#cccccc] px-3 text-[#323c44] hover:bg-[#f5f5f5]" onClick={() => setFyWindow(1)} data-testid="preset-next-fy-button">
              Next FY
            </Button>
            <Button variant="outline" className="h-9 rounded-sm border-[#cccccc] px-3 text-[#323c44] hover:bg-[#f5f5f5]" onClick={onReset} data-testid="clear-filters-button">
              <RotateCcw className="h-4 w-4" />
              Clear
            </Button>
            <Button className="h-9 rounded-sm bg-[#50e3c2] px-3 text-[#323c44] hover:bg-[#57c1a9]" onClick={onApply} data-testid="apply-filters-button">
              Apply filters
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 bg-[#f5f5f5] p-5">
        {/* Filter toolbar row */}
        <div className="flex flex-wrap items-center gap-2" data-testid="filter-toolbar-row">

          {/* Always-visible hierarchical pipeline + stage filter */}
          <PipelineStageFilter
            filterOptions={filterOptions}
            filters={filters}
            onChange={onChange}
          />

          {/* User-added deal / line-item property filters */}
          {userActiveKeys.map((key) => {
            const isDealProperty = key.startsWith("dp:");
            if (isDealProperty) {
              const propName = key.slice(3);
              const meta = getPropertyMeta(key);
              const dpValues = filters.dynamicFilters?.[propName] || [];
              return (
                <TickBoxFilter
                  key={key}
                  label={meta?.label || propName.replace(/_/g, " ")}
                  options={meta?.values || []}
                  values={dpValues}
                  onToggle={(value) => onToggleDynamic(propName, value)}
                  onRemove={() => onRemoveProperty(key)}
                  removable
                  allowIncludeEmpty
                  testId={`dp-filter-${propName}`}
                />
              );
            }

            const isLineItemProperty = key.startsWith("li:");
            if (isLineItemProperty) {
              const propName = key.slice(3);
              const meta = getPropertyMeta(key);
              const liValues = filters.lineItemFilters?.[propName] || [];
              return (
                <TickBoxFilter
                  key={key}
                  label={meta?.label || `${propName.replace(/_/g, " ")} (line item)`}
                  options={meta?.values || []}
                  values={liValues}
                  onToggle={(value) => onToggleLineItem?.(propName, value)}
                  onRemove={() => onRemoveProperty(key)}
                  removable
                  allowIncludeEmpty
                  testId={`li-filter-${propName}`}
                />
              );
            }

            // Standard non-hardcoded filters (owners, companies, products, billingFrequencies)
            const standardFilterMap = {
              owners: { label: "Owners", field: "owners", options: filterOptions.owners },
              companies: { label: "Companies", field: "companies", options: filterOptions.companies },
              products: { label: "Products", field: "products", options: filterOptions.products },
              billingFrequencies: { label: "Billing frequency", field: "billingFrequencies", options: filterOptions.billing_frequencies },
            };
            const std = standardFilterMap[key];
            if (!std) return null;
            return (
              <TickBoxFilter
                key={key}
                label={std.label}
                options={std.options || []}
                values={filters[std.field] || []}
                onToggle={(value) => {
                  const cur = filters[std.field] || [];
                  const has = cur.includes(value);
                  onChange(std.field, has ? cur.filter((v) => v !== value) : [...cur, value]);
                }}
                onRemove={() => onRemoveProperty(key)}
                removable
                testId={`${key}-filter`}
              />
            );
          })}

          <PropertySelector
            options={availableProperties}
            activeKeys={[...HARDCODED_KEYS, ...(activePropertyKeys || [])]}
            disabledKeys={HARDCODED_KEYS}
            onSelect={onAddProperty}
            onRefresh={onRefreshProperties}
            testId="property-selector"
          />

          <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-[#cccccc] bg-white px-3 py-2 text-sm font-medium text-[#323c44] transition hover:bg-[#f5f5f5]" data-testid="include-one-time-toggle">
            <input
              type="checkbox"
              checked={filters.includeOneTime}
              onChange={() => onChange("includeOneTime", !filters.includeOneTime)}
              className="h-4 w-4 rounded border-[#cccccc] accent-[#50e3c2]"
              data-testid="include-one-time-checkbox"
            />
            <span>Include one-time</span>
          </label>


          <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-[#cccccc] bg-white px-3 py-2 text-sm font-medium text-[#323c44] transition hover:bg-[#f5f5f5]" data-testid="collapse-products-toggle" title="Roll product variants up to their reporting group (all CRS → CRS, all Candid → Candid ATS)">
            <input
              type="checkbox"
              checked={filters.collapseProducts || false}
              onChange={() => onChange("collapseProducts", !filters.collapseProducts)}
              className="h-4 w-4 rounded border-[#cccccc] accent-[#50e3c2]"
              data-testid="collapse-products-checkbox"
            />
            <span>Group products</span>
          </label>

          <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-[#cccccc] bg-white px-3 py-2 text-sm font-medium text-[#323c44] transition hover:bg-[#f5f5f5]" data-testid="exclude-non-xero-toggle" title="Only count line items that carry a Xero/invoice stamp — drops non-Xero lines and forecasts">
            <input
              type="checkbox"
              checked={filters.excludeNonXero || false}
              onChange={() => onChange("excludeNonXero", !filters.excludeNonXero)}
              className="h-4 w-4 rounded border-[#cccccc] accent-[#50e3c2]"
              data-testid="exclude-non-xero-checkbox"
            />
            <span>Exclude non-Xero</span>
          </label>

        </div>

        {/* Date & probability row */}
        <div className="grid gap-3 xl:grid-cols-[1fr_1fr_1.1fr]">
          {/* Reporting window — month dropdowns */}
          <div className="grid gap-3 rounded-md border border-[#cccccc] bg-white p-4 sm:grid-cols-2" data-testid="reporting-window-card">
            <DateDropdown
              label="From month"
              value={filters.fromMonth}
              onChange={(v) => onChange("fromMonth", v)}
              presets={MONTH_PRESETS}
              inputType="month"
              placeholder="Any"
              testId="from-month-input"
            />
            <DateDropdown
              label="To month"
              value={filters.toMonth}
              onChange={(v) => onChange("toMonth", v)}
              presets={MONTH_PRESETS}
              inputType="month"
              placeholder="Any"
              testId="to-month-input"
            />
          </div>

          {/* Probability */}
          <div className="rounded-md border border-[#cccccc] bg-white p-4" data-testid="probability-filter-card">
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <label className="space-y-2 text-sm text-[#4a5b68]" data-testid="probability-min-group">
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Min probability</span>
                <input
                  type="number"
                  min={filterOptions.probability_range?.min ?? 0}
                  max={filterOptions.probability_range?.max ?? 100}
                  value={filters.probabilityMin}
                  onChange={(event) => onChange("probabilityMin", event.target.value)}
                  className="h-10 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 text-[#323c44]"
                  placeholder="0"
                  data-testid="probability-min-input"
                />
              </label>
              <label className="space-y-2 text-sm text-[#4a5b68]" data-testid="probability-max-group">
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Max probability</span>
                <input
                  type="number"
                  min={filterOptions.probability_range?.min ?? 0}
                  max={filterOptions.probability_range?.max ?? 100}
                  value={filters.probabilityMax}
                  onChange={(event) => onChange("probabilityMax", event.target.value)}
                  className="h-10 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 text-[#323c44]"
                  placeholder="100"
                  data-testid="probability-max-input"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onQuickSetProbability?.(75)}
                className="rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:bg-[#efefef] transition"
                data-testid="probability-75-button"
              >
                75%+
              </button>
              <button
                type="button"
                onClick={() => onQuickSetProbability?.(90)}
                className="rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:bg-[#efefef] transition"
                data-testid="probability-90-button"
              >
                90%+
              </button>
              <button
                type="button"
                onClick={() => onQuickSetProbability?.(100, 100)}
                className="rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:bg-[#efefef] transition"
                data-testid="probability-100-button"
                title="Worst case — closed/won only"
              >
                Won only
              </button>
            </div>
          </div>

          {/* Close date — date dropdowns */}
          <div className="grid gap-3 rounded-md border border-[#cccccc] bg-white p-4 sm:grid-cols-2" data-testid="close-date-filter-card">
            <DateDropdown
              label="Close date from"
              value={filters.closeDateFrom}
              onChange={(v) => onChange("closeDateFrom", v)}
              presets={DATE_PRESETS}
              inputType="date"
              placeholder="Any"
              testId="close-date-from-input"
            />
            <DateDropdown
              label="Close date to"
              value={filters.closeDateTo}
              onChange={(v) => onChange("closeDateTo", v)}
              presets={DATE_PRESETS}
              inputType="date"
              placeholder="Any"
              testId="close-date-to-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
